import styled from "styled-components"

import { ErrorLogIcon } from "@minuthq/meatball-ui-icons"
import { Text } from "@minuthq/meatball-ui-react"

import {
  CardStatusRow,
  MonitoringCard,
  StatusLeft,
} from "src/components/Homes/HomeDetails/Overview/MonitoringCard"
import { CrowdDetectContent } from "src/components/Homes/HomeDetails/PresenceAndCrowdDetection/CrowdDetectContent"
import { PresenceAndCrowdDetectButton } from "src/components/Homes/HomeDetails/PresenceAndCrowdDetection/PresenceAndCrowdDetectButton"
import { THome } from "src/data/homes/types/homeTypes"
import { getOrgAccessRank } from "src/data/organizations/logic/organizationAccess"
import { OrganizationRank } from "src/data/organizations/types/organizationMemberTypes"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { langKeys } from "src/i18n/langKeys"
import { useTranslate } from "src/i18n/useTranslate"
import CalibratingIcon from "src/ui/icons/calibrating.svg"
import OccupancyIcon from "src/ui/icons/crowd.svg"
import { MInfo } from "src/ui/Info/MInfo"
import { localizedDate } from "src/ui/LocalizedDate/localized-date"
import { spacing } from "src/ui/spacing"
import { extractHHMM, formatDateAsTime } from "src/utils/l10n"

export function PresenceAndCrowdDetectCard({ home }: { home: THome }) {
  const canToggleOccupancy =
    getOrgAccessRank(home.user_role) >= OrganizationRank.ADMIN
  const { t } = useTranslate()

  const occupancy = home.occupancy
  const {
    status,
    active,
    latest_scanned_devices: detected,
    scanned_devices_threshold: threshold,
    latest_scanned_devices_updated_at: lastUpdate,
  } = occupancy || {}

  const presenceDetection = home.motion_automation

  const {
    mode: presenceDetectionMode,
    last_unexpected_presence_at,
    has_unexpected_presence,
  } = presenceDetection || {}

  const aboveThreshold = status === "crowd_detected"

  const isAnyFeatureActive = !!active || presenceDetectionMode === "enabled"

  const alertTime = formatDateAsTime({
    date: last_unexpected_presence_at || "",
    clockType: useGetUser().clock_type,
    excludeSeconds: true,
    timezone: home.timezone,
  })

  function LeftCol() {
    const statusContents = isAnyFeatureActive ? t(langKeys.on) : t(langKeys.off)
    const clockType = useGetUser().clock_type

    const time = lastUpdate
      ? extractHHMM(
          new Date(localizedDate(lastUpdate, clockType, home.timezone)),
          clockType
        )
      : ""

    return (
      <StatusLeft
        icon={<OccupancyIcon width={32} height={32} />}
        title={t(langKeys.motion_and_occupancy_detection)}
        statusContents={statusContents}
        isOn={isAnyFeatureActive}
        statusInfo={
          active ? <CrowdRiskDescription time={time} status={status} /> : null
        }
      />
    )
  }

  return (
    <MonitoringCard
      titleSlots={[
        <LeftCol key="left" />,
        <PresenceAndCrowdDetectButton
          key="right"
          motionAutomation={presenceDetection}
          violations={home.violations}
          homeData={home}
          toggleAllowed={canToggleOccupancy}
          analyticsContext="unit_overview"
        />,
      ]}
      cardFooter={{
        showTopBorder: status === "waiting_for_data",
        component: <OccupancyCardFooter status={status} />,
      }}
    >
      <CardWrapper>
        {presenceDetectionMode === "enabled" && has_unexpected_presence && (
          <CardStatusRow
            icon={<ErrorLogIcon color="emergency" width={24} />}
            subtitle={t(langKeys.motion_automation_motion_during_vacancy)}
            color="emergency"
            body={
              <StatusColumnRow>
                {t(
                  langKeys.motion_automation_unauthorized_presence_detected_at,
                  {
                    time: alertTime,
                  }
                )}
              </StatusColumnRow>
            }
            border
          />
        )}

        {active && (
          <CrowdDetectContent
            status={status}
            threshold={threshold}
            detected={detected}
            aboveThreshold={aboveThreshold}
            lastUpdate={lastUpdate}
            homeId={home.home_id}
            timezone={home.timezone}
          />
        )}
      </CardWrapper>
    </MonitoringCard>
  )
}

function CrowdRiskDescription({
  status,
  time,
}: {
  status: NonNullable<THome["occupancy"]>["status"]
  time: string
}) {
  const { t } = useTranslate()

  const showDescription = status === "crowd_detected" || status === "processing"

  if (!showDescription) {
    return null
  }

  return (
    <CrowdRiskBox>
      {status === "crowd_detected" && (
        <Text color="emergency">
          {t(langKeys.crowd_detect_active_emergency_title, { 0: time })}
        </Text>
      )}

      {status === "processing" && (
        <Text color="good">
          {t(langKeys.crowd_detect_active_normal_title, { 0: time })}
        </Text>
      )}

      <MInfo content={t(langKeys.crowd_detect_crowd_risk_description)} />
    </CrowdRiskBox>
  )
}

function OccupancyCardFooter({
  status,
}: {
  status: NonNullable<THome["occupancy"]>["status"]
}) {
  const { t } = useTranslate()

  if (status !== "waiting_for_data") {
    return null
  }

  return (
    <CardStatusRow
      icon={<CalibratingIcon width={24} />}
      subtitle={t(langKeys.crowd_detect_analyzing_title)}
      body={t(langKeys.crowd_detect_analyzing_short_description)}
    />
  )
}

const CrowdRiskBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.M};
`

const StatusColumnRow = styled.div<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? "none" : "flex")};
  gap: ${spacing.XS2};
`

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.L};
  padding-top: ${spacing.L};
`
